if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      this.filteringOption = this.dataset.filteringOption;
      
      // Parse variant gallery data
      this.variantGalleryData = null;
      const variantGalleryScript = this.querySelector('[id^="VariantGalleryData-"]');
      if (variantGalleryScript) {
        try {
          this.variantGalleryData = JSON.parse(variantGalleryScript.textContent);
        } catch (e) {
          console.error('Failed to parse variant gallery data:', e);
        }
      }

      // Subscribe to variant changes
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS['variantChange'], (event) => {
          if (event.data && event.data.sectionId === this.dataset.section && event.data.variant) {
            this.handleVariantChange(event.data.variant);
          }
        });
      }

      if (this.elements.viewer) {
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      }

      if (this.elements.thumbnails) {
        // Use event delegation on the thumbnails container for better performance
        // This way we don't need to reattach listeners when content changes
        this.elements.thumbnails.addEventListener('click', (e) => {
          const thumbnailItem = e.target.closest('[data-target]');
          if (thumbnailItem) {
            const mediaId = thumbnailItem.getAttribute('data-target');
            if (mediaId) {
              e.preventDefault();
              this.setActiveMedia(mediaId, false);
            }
          }
        });
        
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (!this.elements.viewer) return;

      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach(slide => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        })
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) {
        console.warn(`Media with id ${mediaId} not found`);
        return;
      }

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, false, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    handleVariantChange(variant) {
      if (!this.variantGalleryData || !variant || !variant.id) {
        return;
      }

      const variantId = variant.id.toString();
      const variantGallery = this.variantGalleryData.variants[variantId];
      
      // Get images to display: variant gallery if available, otherwise product media
      let imagesToDisplay = [];
      if (variantGallery && variantGallery.gallery && variantGallery.gallery.length > 0) {
        imagesToDisplay = variantGallery.gallery;
      } else if (this.variantGalleryData.productMedia) {
        imagesToDisplay = this.variantGalleryData.productMedia;
      }

      if (imagesToDisplay.length === 0) return;

      try {
        this.updateSliderContent(imagesToDisplay);
      } catch (error) {
        console.error('Error updating slider content:', error);
      }
    }

    updateSliderContent(images) {
      if (!this.elements.viewer) return;

      const sectionId = this.dataset.section;
      // Check for duplicate by looking at viewer id
      const idAppend = this.elements.viewer.id && this.elements.viewer.id.includes('-duplicate') ? '-duplicate' : '';
      const viewerSlider = this.elements.viewer.querySelector('ul[id^="Slider-Gallery-"]');
      const thumbnailSlider = this.elements.thumbnails ? this.elements.thumbnails.querySelector('ul[id^="Slider-Thumbnails-"]') : null;

      if (!viewerSlider) return;

      // Store slider instances before clearing
      const viewerSliderInstance = this.elements.viewer.slider;
      const thumbnailSliderInstance = this.elements.thumbnails ? this.elements.thumbnails.slider : null;

      // Clear existing slides
      viewerSlider.innerHTML = '';
      if (thumbnailSlider) thumbnailSlider.innerHTML = '';

      // Create new slides
      images.forEach((image, index) => {
        const imageId = image.id || image.mediaId || `img-${index}`;
        const mediaId = `${sectionId}-${imageId}`;
        const isActive = index === 0;

        // Create main slide
        const slide = this.createMediaSlide(image, mediaId, sectionId, idAppend, isActive, imageId);
        viewerSlider.appendChild(slide);

        // Create thumbnail slide if thumbnails exist
        if (thumbnailSlider) {
          const thumbnail = this.createThumbnailSlide(image, mediaId, sectionId, idAppend, index + 1, isActive);
          thumbnailSlider.appendChild(thumbnail);
        }
      });

      // Wait for DOM to update, then reinitialize slider
      // Use setTimeout to ensure DOM is fully updated
      setTimeout(() => {
        // Reinitialize slider - try multiple approaches
        if (this.elements.viewer) {
          // If slider was destroyed, we need to reinitialize it
          // The slider-component should handle this, but we can help by calling initPages
          if (this.elements.viewer.initPages) {
            this.elements.viewer.initPages();
          }
          
          // Update slider if it exists
          if (this.elements.viewer.slider) {
            if (typeof this.elements.viewer.slider.refresh === 'function') {
              this.elements.viewer.slider.refresh();
            }
            if (typeof this.elements.viewer.slider.update === 'function') {
              this.elements.viewer.slider.update();
            }
          }
          
          // If update method exists on the component, call it
          if (this.elements.viewer.update) {
            this.elements.viewer.update();
          }
        }

        // Reinitialize thumbnail slider
        if (this.elements.thumbnails) {
          if (this.elements.thumbnails.initPages) {
            this.elements.thumbnails.initPages();
          }
          if (this.elements.thumbnails.slider) {
            if (typeof this.elements.thumbnails.slider.refresh === 'function') {
              this.elements.thumbnails.slider.refresh();
            }
            if (typeof this.elements.thumbnails.slider.update === 'function') {
              this.elements.thumbnails.slider.update();
            }
          }
          if (this.elements.thumbnails.update) {
            this.elements.thumbnails.update();
          }
        }

        // Event listeners are already attached via event delegation in constructor
        // No need to reattach them here

        // Set first slide as active after slider is ready
        if (images.length > 0) {
          const firstImageId = images[0].id || images[0].mediaId || 'img-0';
          const firstMediaId = `${sectionId}-${firstImageId}`;
          // Wait a bit more to ensure slider is ready
          setTimeout(() => {
            const firstSlide = this.elements.viewer.querySelector(`[data-media-id="${firstMediaId}"]`);
            if (firstSlide) {
              try {
                this.setActiveMedia(firstMediaId, false);
              } catch (error) {
                console.error('Error setting active media:', error);
              }
            }
          }, 100);
        }
      }, 50);
    }

    createMediaSlide(image, mediaId, sectionId, idAppend, isActive, imageId) {
      const li = document.createElement('li');
      li.id = `Slide-${sectionId}-${imageId}${idAppend}`;
      li.className = `product__media-item grid__item slider__slide${isActive ? ' is-active' : ''}`;
      li.setAttribute('data-media-id', mediaId);
      li.setAttribute('data-alt', image.alt || '');

      const aspectRatio = image.height && image.width ? (image.height / image.width) : 1;
      const container = document.createElement('div');
      container.className = 'product-media-container media-type-image media-fit-contain global-media-settings gradient';
      container.style.cssText = `--ratio: ${aspectRatio}; --preview-ratio: ${aspectRatio};`;

      const modalOpener = document.createElement('modal-opener');
      modalOpener.className = 'product__modal-opener product__modal-opener--image no-js-hidden';
      modalOpener.setAttribute('data-modal', `#ProductModal-${sectionId}`);

      const icon = document.createElement('span');
      icon.className = 'product__media-icon motion-reduce quick-add-hidden product__media-icon--none';
      icon.setAttribute('aria-hidden', 'true');

      const mediaDiv = document.createElement('div');
      mediaDiv.className = 'product__media media media--transparent';

      const img = document.createElement('img');
      img.src = image.src;
      img.alt = image.alt || '';
      img.loading = isActive ? 'eager' : 'lazy';
      img.width = image.width || '';
      img.height = image.height || '';
      img.className = 'image-magnify-none';

      mediaDiv.appendChild(img);
      modalOpener.appendChild(icon);
      modalOpener.appendChild(mediaDiv);

      const toggleButton = document.createElement('button');
      toggleButton.className = 'product__media-toggle quick-add-hidden';
      toggleButton.type = 'button';
      toggleButton.setAttribute('aria-haspopup', 'dialog');
      toggleButton.setAttribute('data-media-id', imageId);
      toggleButton.innerHTML = `<span class="visually-hidden">Open media ${mediaId}</span>`;

      modalOpener.appendChild(toggleButton);
      container.appendChild(modalOpener);
      li.appendChild(container);

      return li;
    }

    createThumbnailSlide(image, mediaId, sectionId, idAppend, position, isActive) {
      const li = document.createElement('li');
      li.id = `Slide-Thumbnails-${sectionId}-${position}${idAppend}`;
      li.className = 'thumbnail-list__item slider__slide';
      li.setAttribute('data-target', mediaId);
      li.setAttribute('data-media-position', position);
      li.setAttribute('data-alt', image.alt || '');

      const thumbnailId = `Thumbnail-${sectionId}-${position}${idAppend}`;
      const button = document.createElement('button');
      button.className = 'thumbnail global-media-settings global-media-settings--no-shadow';
      button.setAttribute('aria-label', `Load image ${position}`);
      if (isActive) button.setAttribute('aria-current', 'true');
      button.setAttribute('aria-controls', `GalleryViewer-${sectionId}${idAppend}`);
      button.setAttribute('aria-describedby', thumbnailId);

      const img = document.createElement('img');
      img.id = thumbnailId;
      img.src = image.src;
      img.alt = image.alt || '';
      img.loading = 'lazy';
      img.width = image.width || 416;
      img.height = image.height || '';
      img.style.width = '100%';
      img.style.height = 'auto';

      button.appendChild(img);
      li.appendChild(button);

      return li;
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
    }
  });
}
